/**
 * Click ID Tracking (GCLID, FBCLID, MSCLKID)
 * Captures ad platform click IDs from URL params and stores them for 30 days
 * These are critical for offline conversion import back to Google Ads / Meta / Bing
 */

const CLICK_ID_STORAGE_KEY = 'solar_click_ids';
const CLICK_ID_EXPIRY_DAYS = 30;

export interface ClickIdData {
  gclid?: string;
  fbclid?: string;
  msclkid?: string;
  captured_at?: string;
}

/**
 * Capture click IDs from URL parameters
 * Should be called on every page load
 */
export const captureClickIds = (): void => {
  if (typeof window === 'undefined') return;

  const urlParams = new URLSearchParams(window.location.search);
  const gclid = urlParams.get('gclid');
  const fbclid = urlParams.get('fbclid');
  const msclkid = urlParams.get('msclkid');

  // Only update if we have new click IDs
  if (!gclid && !fbclid && !msclkid) return;

  const existing = getStoredClickIds();
  const updated: ClickIdData = {
    ...existing,
    captured_at: new Date().toISOString(),
  };

  // Latest click ID wins (last-click attribution for ad platforms)
  if (gclid) updated.gclid = gclid;
  if (fbclid) updated.fbclid = fbclid;
  if (msclkid) updated.msclkid = msclkid;

  localStorage.setItem(CLICK_ID_STORAGE_KEY, JSON.stringify(updated));
  console.log('[Click ID Tracking] Captured:', {
    gclid: !!gclid,
    fbclid: !!fbclid,
    msclkid: !!msclkid,
  });
};

/**
 * Get stored click IDs (with 30-day expiry check)
 */
export const getStoredClickIds = (): ClickIdData => {
  if (typeof window === 'undefined') return {};

  try {
    const stored = localStorage.getItem(CLICK_ID_STORAGE_KEY);
    if (!stored) return {};

    const data: ClickIdData = JSON.parse(stored);

    // Check expiry (30 days)
    if (data.captured_at) {
      const capturedAt = new Date(data.captured_at).getTime();
      const expiryMs = CLICK_ID_EXPIRY_DAYS * 24 * 60 * 60 * 1000;
      if (Date.now() - capturedAt > expiryMs) {
        localStorage.removeItem(CLICK_ID_STORAGE_KEY);
        return {};
      }
    }

    return data;
  } catch {
    return {};
  }
};

// Auto-capture on module load to catch click IDs before any React redirects
captureClickIds();
